import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const API_URL = (
  import.meta.env.VITE_API_URL || 'https://dev.api.giotheapp.com'
).replace(/\/$/, '');

/**
 * Public permanent invoice share page (no login).
 * Full-page invoice PDF only — browser PDF controls handle download.
 */
const InvoiceSharePage = () => {
  const { token: rawToken } = useParams<{ token: string }>();
  const token = rawToken?.trim() ?? '';
  const [pdfSrc, setPdfSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(
    token ? null : 'This invoice link is invalid.'
  );

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    const run = async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/public/invoice-shares/${encodeURIComponent(token)}`,
          { headers: { Accept: 'application/json' } }
        );
        if (cancelled) return;
        if (!res.ok) {
          setError(
            res.status === 404
              ? 'This invoice link is invalid or no longer available.'
              : 'Unable to load this invoice.'
          );
          return;
        }
        const data = (await res.json()) as { pdfUrl?: string };
        const path = String(data.pdfUrl || '').trim();
        if (!path) {
          setError('Unable to load this invoice.');
          return;
        }
        setPdfSrc(path.startsWith('http') ? path : `${API_URL}${path}`);
      } catch {
        if (!cancelled) setError('Unable to load this invoice.');
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white px-4">
        <p className="text-center text-sm text-gray-600">{error}</p>
      </div>
    );
  }

  if (!pdfSrc) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <p className="text-sm text-gray-500">Loading invoice…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <iframe
        title="Invoice"
        src={pdfSrc}
        className="h-screen w-full border-0"
      />
    </div>
  );
};

export default InvoiceSharePage;
